import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Users, Search, Shield, Building2, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Input } from '@/components/ui/Input';
import { PageLayout } from '@/components/ui/PageLayout';

interface User {
  id: string;
  email: string;
  name: string | null;
  primary_role: string;
  created_at: string;
  clinic_count: number;
}

export function AdminUsers() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const { data: users, isLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('users')
        .select(
          `
 id,
 email,
 name,
 primary_role,
 created_at
 `,
        )
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get clinic membership counts separately
      const userIds = (data ?? []).map((u) => u.id);
      const { data: memberships } = await supabase
        .from('clinic_memberships')
        .select('user_id')
        .in('user_id', userIds);

      const countMap = new Map<string, number>();
      (memberships ?? []).forEach((m) => {
        countMap.set(m.user_id, (countMap.get(m.user_id) ?? 0) + 1);
      });

      return (data ?? []).map((u) => ({
        ...u,
        clinic_count: countMap.get(u.id) ?? 0,
      })) as User[];
    },
  });

  const filteredUsers = users?.filter(
    (u) =>
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      u.name?.toLowerCase().includes(search.toLowerCase()),
  );

  const platformAdmins = users?.filter((u) => u.primary_role === 'platform_admin').length ?? 0;

  return (
    <PageLayout title={t('admin.users.title')} subtitle={t('admin.users.subtitle')}>

      {/* Search */}
      <div className="mb-6 flex items-center gap-4">
        <div className="flex-1">
          <Input
            type="text"
            placeholder={t('admin.users.search_placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leadingIcon={<Search className="h-4 w-4" aria-hidden />}
          />
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-ds-muted" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-ds-line bg-ds-card">
          <table className="min-w-full divide-y divide-ds-line">
            <thead className="bg-ds-sunk">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-ds-muted">
                  {t('admin.users.col_user')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-ds-muted">
                  {t('admin.users.col_role')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-ds-muted">
                  {t('admin.users.col_clinics')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-ds-muted">
                  {t('admin.users.col_created')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ds-line">
              {filteredUsers?.map((user) => (
                <tr key={user.id} className="hover:bg-ds-row-hover">
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-ds-neutral-tint">
                        <Users className="h-5 w-5 text-ds-muted" />
                      </div>
                      <div>
                        <p className="font-medium text-ds-text">{user.name || user.email}</p>
                        {user.name && <p className="text-xs text-ds-muted">{user.email}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    {user.primary_role === 'platform_admin' ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-ds-amber-tint px-2 py-1 text-xs font-medium text-ds-amber-ink">
                        <Shield className="h-3 w-3" />
                        {t('admin.users.role_platform_admin')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-ds-teal-tint px-2 py-1 text-xs font-medium text-ds-teal">
                        <Building2 className="h-3 w-3" />
                        {t('admin.users.role_clinic_user')}
                      </span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-ds-text">
                    {user.clinic_count}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-ds-muted">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
              {filteredUsers?.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-sm text-ds-muted">
                    {t('admin.users.no_users')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Stats */}
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-ds-line bg-ds-card p-4">
          <p className="text-sm text-ds-muted">{t('admin.users.total_users')}</p>
          <p className="mt-1 text-2xl font-bold text-ds-text">{users?.length ?? 0}</p>
        </div>
        <div className="rounded-lg border border-ds-line bg-ds-card p-4">
          <p className="text-sm text-ds-muted">{t('admin.users.platform_admins')}</p>
          <p className="mt-1 text-2xl font-bold text-ds-amber">{platformAdmins}</p>
        </div>
        <div className="rounded-lg border border-ds-line bg-ds-card p-4">
          <p className="text-sm text-ds-muted">{t('admin.users.clinic_users')}</p>
          <p className="mt-1 text-2xl font-bold text-ds-teal">
            {(users?.length ?? 0) - platformAdmins}
          </p>
        </div>
      </div>
    </PageLayout>
  );
}
